import {
  Copy,
  Flex,
  Loader,
  Square,
  Squish,
  Stack,
  SvgCheck,
  SvgMagnifyingGlass,
} from '@grubhubprod/gh-cookbook-react';
import React, { useEffect, useMemo, useRef, useState, forwardRef } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import unionBy from 'lodash/unionBy';
import { Input } from 'src/components/Input/Input';
import { useKeyPress } from 'src/components/SelectSearch/utils';
import { useDebouncedValue } from 'src/utilities/useDebouncedValue';
import { createSelectedItemsMap } from 'src/components/SelectSearch/utils/createSelectedItemsMap';
import { filterItemsBySearchTerm } from 'src/components/SelectSearch/utils/filterHelpers';
import {
  SearchResourceOptions,
  ISearchParams,
  MAX_PAGE_SIZE,
  SearchStatus,
  IDepartment,
  UNASSIGNED_DEPARTMENT_NAME,
} from 'src/utilities/useSearchHooks';
import { MODAL_Z_INDEX } from 'src/components/Modal/Modal';

const DEBOUNCE_SEARCH_TIMING = 200;

export interface SelectSearchMenuProps<T, M> {
  corpId?: string;
  keyName: keyof T;
  displayProp: keyof T;
  isRequiredDepartmentSearch?: boolean; // hacky way to prevent "Unassigned" department
  searchInputPlaceholder?: string;
  selectedItems: T[];
  updateSelectedItems: (selectedItems: T[]) => void;
  isMultiSelect?: boolean;
  onValueChange?: (value: M extends true ? T[] : T) => void;
  searchResource?: SearchResourceOptions;
  filterSearchResults?: (item: T) => boolean;
  getNoResultsMessage?: (query: string) => string | JSX.Element;
  style?: React.CSSProperties;
  maxMenuHeight?: string;
}

type SelectSearchMenuWithRefProps = <T, M>(
  props: SelectSearchMenuProps<T, M> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  }
) => ReturnType<typeof SelectSearchMenu>;

function SelectSearchMenu<T, M>(
  {
    displayProp,
    searchResource,
    selectedItems = [],
    isRequiredDepartmentSearch,
    updateSelectedItems,
    onValueChange,
    keyName,
    isMultiSelect,
    corpId,
    style,
    filterSearchResults,
    getNoResultsMessage,
    maxMenuHeight = '200px',
  }: SelectSearchMenuProps<T, M>,
  ref: React.ForwardedRef<HTMLDivElement>
): JSX.Element {
  const downPress = useKeyPress('ArrowDown');
  const upPress = useKeyPress('ArrowUp');
  const enterPress = useKeyPress('Enter');
  const [cursor, setCursor] = useState(0);
  const [hovered, setHovered] = useState(undefined);
  const [items, setItems] = useState([]);
  const { register, setValue, watch } = useForm({
    mode: 'onTouched',
  });
  const searchInput = useRef(null);

  const debouncedSearchTerm = useDebouncedValue(
    watch('searchTerm', '') as string,
    DEBOUNCE_SEARCH_TIMING
  );

  const {
    doSearch,
    resultsList: options,
    searchStatus,
    unsubscribeFromSearch,
    subscribeToSearch,
  } = searchResource(corpId);

  const selectedItemsMap = useMemo(
    () => createSelectedItemsMap(selectedItems, keyName),
    [selectedItems, keyName]
  );

  useEffect(() => {
    if (searchInput.current) {
      searchInput.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput.current]);

  useEffect(() => {
    const filteredItems = (options as unknown as T[]).filter((option) => {
      let keepOption = true;

      if (selectedItemsMap.size > 0) {
        // filter out options that are already selected
        keepOption = !selectedItemsMap.has(String(option[keyName]));
      }

      // hacky way to prevent Unassigned deparment showing up
      if (
        isRequiredDepartmentSearch &&
        (option as unknown as IDepartment).name === UNASSIGNED_DEPARTMENT_NAME
      ) {
        keepOption = false;
      }

      if (filterSearchResults) {
        keepOption = filterSearchResults(option);
      }

      return keepOption;
    });

    // filter selected items by search term
    const filteredSelectedItems = filterItemsBySearchTerm(
      selectedItems,
      debouncedSearchTerm,
      displayProp
    );

    setItems(unionBy([...filteredSelectedItems, ...filteredItems], keyName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedItems,
    options,
    debouncedSearchTerm,
    selectedItemsMap,
    displayProp,
  ]);

  useEffect(() => {
    if (items.length && downPress) {
      setCursor((prevState) =>
        prevState < items.length - 1 ? prevState + 1 : prevState
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downPress]);

  useEffect(() => {
    if (items.length && upPress) {
      setCursor((prevState) => (prevState > 0 ? prevState - 1 : prevState));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upPress]);

  useEffect(() => {
    if (items.length && enterPress && cursor > -1) {
      toggleItemSelection(items[cursor]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterPress]);

  useEffect(() => {
    if (items.length && hovered) {
      setCursor(items.findIndex((item) => item[keyName] === hovered[keyName]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered]);

  useEffect(() => {
    updateSearch(debouncedSearchTerm);
    setCursor(0);

    return () => {
      // Make sure you re-subscribe to search if you're using unsubscribeFromSearch
      unsubscribeFromSearch();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  function updateSearch(searchQuery: string): void {
    const searchParams: ISearchParams = { size: MAX_PAGE_SIZE };

    if (searchQuery) {
      searchParams.query = searchQuery;
    }

    subscribeToSearch();
    doSearch(searchParams);
  }

  function onItemClick(item) {
    // We need to use setTimeout to prevent closing the dropdown on document click.
    // Since the clicked item is filtered out, the click would be considered outside of the dropdown container
    // which would make the dropdown close, so we need to artificially wait until that check has happened
    setTimeout(() => {
      updateSelectedItems(isMultiSelect ? [...selectedItems, item] : [item]);

      if (onValueChange) {
        onValueChange(isMultiSelect ? [...selectedItems, item] : item);
      }
      setValue('searchTerm', '');
    }, 0);
  }

  function removeItem(item) {
    // We need to use setTimeout to prevent closing dropdown on document click.
    // Since the item is removed, the click would be considered outside of the dropdown container
    // which would make the dropdown close, so we need to artificially wait until that check has happened
    setTimeout(() => {
      const filteredSelectedItems = selectedItems.filter(
        (i) => i[keyName] !== item[keyName]
      );
      updateSelectedItems(filteredSelectedItems);
      if (onValueChange) {
        onValueChange(isMultiSelect ? filteredSelectedItems : item);
      }
      setValue('searchTerm', '');
    }, 0);
  }

  function toggleItemSelection(item: T): void {
    // in rare cases the item may be undefined and we need
    // to guard against that to not JS throw errors
    if (!item) {
      return;
    }

    const action = selectedItemsMap.has(String(item[keyName]))
      ? removeItem
      : onItemClick;

    action(item);
  }

  return (
    <Flex
      flexDirection="column"
      dimension="4"
      zIndex={MODAL_Z_INDEX + 1}
      bg="globals.white"
      borderRadius="4px"
      overflow="auto"
      position="fixed"
      maxHeight={maxMenuHeight}
      style={style}
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Input
        inputStyle={{
          border: 'none',
          borderRadius: 0,
          backgroundColor: '#F5F3F1',
          outline: 'none',
        }}
        key="searchTerm"
        ariaLabel="Search"
        placeholder="Search"
        name="searchTerm"
        id="searchTerm"
        prefixIcon={
          <Copy variant="body" color="secondaryText">
            <SvgMagnifyingGlass size="16px" />
          </Copy>
        }
        ref={(e: HTMLInputElement): void => {
          register(e);
          // eslint-disable-next-line better-mutation/no-mutation
          searchInput.current = e;
        }}
      />
      {items && items.length > 0 ? (
        items.map((item, i) => (
          <Squish
            data-testid={
              selectedItemsMap.has(item[keyName])
                ? 'search-selected-item'
                : 'search-select-item'
            }
            key={item[keyName]}
            inset="md"
            bg={i === cursor ? 'tintedBackground' : 'untintedBackground'}
            display="block"
            borderBottomStyle="solid"
            borderBottomColor="lightLine"
            borderBottomWidth={1}
            onClick={(): void => toggleItemSelection(item)}
            onMouseEnter={(): void => setHovered(item)}
            onMouseLeave={(): void => setHovered(undefined)}
            className="cursorPointer"
          >
            <Flex width="100%" justifyContent="space-between">
              <Square overflow="hidden">
                <Stack
                  x="xxx-sm"
                  className="ellipsisOverflow"
                  title={item[displayProp]}
                >
                  {item[displayProp]}
                </Stack>
              </Square>
              {selectedItemsMap.has(item[keyName]) && (
                <Stack flexShrink={0}>
                  <Copy variant="body" color="interactive" overflow="hidden">
                    <Flex>
                      <SvgCheck aria-label="remove" size="20px" />
                    </Flex>
                  </Copy>
                </Stack>
              )}
            </Flex>
          </Squish>
        ))
      ) : (
        <Squish
          data-testid="results-container"
          inset="md"
          bg="globals.white"
          display="block"
          borderBottomStyle="solid"
          borderBottomColor="lightLine"
          borderBottomWidth={1}
        >
          {searchStatus === SearchStatus.LOADING ||
          searchStatus === SearchStatus.IDLE ? (
            <Flex
              backgroundColor="globals.white75a"
              alignItems="center"
              justifyContent="center"
            >
              <Loader size="md" />
            </Flex>
          ) : (
            <Square padding="4px">
              <Copy variant="body" display="block" color="secondaryText">
                {getNoResultsMessage
                  ? getNoResultsMessage(debouncedSearchTerm)
                  : 'No results found.'}
              </Copy>
            </Square>
          )}
        </Squish>
      )}
    </Flex>
  );
}

export default forwardRef(SelectSearchMenu) as SelectSearchMenuWithRefProps;
